import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  X,
  Search,
  ArrowRightLeft,
  AlertCircle,
  RefreshCw,
  Check,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { shouldCloseDialog, useEscapeClose } from '@/hooks/useEscapeClose'
import { listMaterialsApi, type Material } from '@/services/materials'
import {
  createFreeConversion,
  type FreeConversionItem,
} from '@/services/warehouse/freeConversionsApi'
import type { FreeStockItem } from '@/services/warehouse/freeStockApi'

interface FreeConversionModalProps {
  item: FreeStockItem | null
  isOpen: boolean
  onClose: () => void
  onSuccess: (conversion: FreeConversionItem) => void
  initialMaterials?: any[]
  initialSelectedMaterialId?: string
  initialFactor?: string
}

export function FreeConversionModal({
  item,
  isOpen,
  onClose,
  onSuccess,
  initialMaterials,
  initialSelectedMaterialId,
  initialFactor,
}: FreeConversionModalProps): React.ReactElement | null {
  const [materials, setMaterials] = useState<Material[]>(
    initialMaterials ? initialMaterials.filter((m: any) => m.status === 'active') : []
  )
  const [loadingMaterials, setLoadingMaterials] = useState(false)
  const [materialSearch, setMaterialSearch] = useState('')

  const [selectedMaterialId, setSelectedMaterialId] = useState<string>(initialSelectedMaterialId ?? '')
  const [qtyFree, setQtyFree] = useState<number>(item?.qty_on_hand ?? 0)
  const [factor, setFactor] = useState<string>(initialFactor ?? '')
  const [reason, setReason] = useState<string>('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [idempotencyKey, setIdempotencyKey] = useState<string>('')

  const hasUnsavedChanges = Boolean(materialSearch.trim() || selectedMaterialId || factor.trim() || reason.trim() || qtyFree !== (item?.qty_on_hand ?? 0))
  const requestClose = useCallback(() => {
    if (submitting || !shouldCloseDialog(hasUnsavedChanges)) return
    onClose()
  }, [hasUnsavedChanges, onClose, submitting])
  useEscapeClose({ enabled: isOpen && Boolean(item), hasUnsavedChanges, onClose: requestClose })

  // Load active materials
  useEffect(() => {
    if (!isOpen) return
    if (initialMaterials !== undefined) {
      setMaterials(initialMaterials.filter((m: any) => m.status === 'active'))
      return
    }
    let isMounted = true
    setLoadingMaterials(true)
    setError(null)

    // New idempotency key per modal session
    setIdempotencyKey(`fc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`)

    listMaterialsApi()
      .then((res) => {
        if (!isMounted) return
        const activeOnly = res.filter((m) => m.status === 'active')
        setMaterials(activeOnly)
      })
      .catch((err) => {
        if (isMounted) setError(err?.message || 'Không thể tải danh sách vật tư')
      })
      .finally(() => {
        if (isMounted) setLoadingMaterials(false)
      })

    return () => {
      isMounted = false
    }
  }, [isOpen])

  // Initialize values when item changes
  useEffect(() => {
    if (item && isOpen) {
      setQtyFree(item.qty_on_hand || 0)
      setSelectedMaterialId('')
      setFactor('')
      setReason('')
      setMaterialSearch('')
      setError(null)
    }
  }, [item, isOpen])

  // Filter active materials by search
  const filteredMaterials = useMemo(() => {
    if (!materialSearch.trim()) return materials
    const q = materialSearch.toLowerCase()
    return materials.filter((m) => m.name.toLowerCase().includes(q))
  }, [materials, materialSearch])

  const selectedMaterial = useMemo(() => {
    return materials.find((m) => m.id === selectedMaterialId) || null
  }, [materials, selectedMaterialId])

  // Check if UOMs match
  const isSameUom = useMemo(() => {
    if (!item || !selectedMaterial) return false
    const freeUom = (item.uom || '').trim().toLowerCase()
    const matUom = (selectedMaterial.uom_default || '').trim().toLowerCase()
    return freeUom === matUom && freeUom.length > 0
  }, [item, selectedMaterial])

  // Expected base quantity
  const expectedQtyBase = useMemo(() => {
    if (isSameUom) return qtyFree
    const f = parseFloat(factor)
    if (Number.isNaN(f) || f <= 0) return null
    return Math.round(qtyFree * f * 10000) / 10000
  }, [qtyFree, factor, isSameUom])

  async function handleSubmit() {
    setError(null)

    if (!item) return
    if (!selectedMaterialId) {
      setError('Vui lòng chọn vật tư đích cần chuyển sang')
      return
    }

    if (!qtyFree || qtyFree <= 0) {
      setError('Số lượng chuyển phải lớn hơn 0')
      return
    }

    if (qtyFree > item.qty_on_hand) {
      setError(`Số lượng chuyển (${qtyFree}) vượt quá tồn hiện tại (${item.qty_on_hand})`)
      return
    }

    let parsedFactor: number | null = null
    if (!isSameUom) {
      const f = parseFloat(factor)
      if (Number.isNaN(f) || f <= 0) {
        setError(
          `Khác đơn vị tính (${item.uom} ≠ ${selectedMaterial?.uom_default}), bắt buộc phải nhập hệ số quy đổi lớn hơn 0`,
        )
        return
      }
      parsedFactor = f
    } else {
      parsedFactor = 1
    }

    if (!reason.trim()) {
      setError('Vui lòng nhập lý do chuyển sang vật tư (bắt buộc)')
      return
    }

    setSubmitting(true)
    const key = idempotencyKey || `fc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

    try {
      const res = await createFreeConversion(
        {
          free_item_id: item.id,
          material_id: selectedMaterialId,
          qty_free: qtyFree,
          factor: parsedFactor,
          reason: reason.trim(),
        },
        key,
      )
      onSuccess(res)
      onClose()
    } catch (err: any) {
      const code = err.code || ''
      const msg = err.message || 'Lỗi khi chuyển đổi hàng tự do sang vật tư'
      if (code === 'INSUFFICIENT_STOCK') {
        setError('Lỗi tồn kho: Tồn hàng tự do không đủ để thực hiện chuyển đổi')
      } else if (code === 'CONVERSION_FACTOR_REQUIRED') {
        setError('Khác đơn vị tính nên bắt buộc phải nhập hệ số quy đổi')
      } else if (code === 'REASON_REQUIRED') {
        setError('Lý do chuyển là bắt buộc')
      } else {
        setError(`${msg} (${code || 'ERROR'})`)
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen || !item) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 backdrop-blur-xs font-sans"
      role="dialog"
      aria-modal="true"
      aria-labelledby="convert-free-modal-title"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-3.5">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-blue-600" />
            <div>
              <h2 id="convert-free-modal-title" className="text-base font-bold text-slate-900">
                Chuyển hàng tự do sang vật tư có sẵn
              </h2>
              <p className="text-xs text-slate-500">
                Chuyển từng dòng vào sổ cái kho vật tư chuẩn
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Đóng"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error banner */}
        {error ? (
          <div className="flex items-center gap-2 border-b border-rose-200 bg-rose-50 px-5 py-2.5 text-xs text-rose-800">
            <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" />
            <span>{error}</span>
          </div>
        ) : null}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Source item info */}
          <div className="rounded-[var(--radius-md)] border border-slate-200 bg-slate-50 p-3">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
              Hàng tự do nguồn
            </span>
            <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-slate-900 text-sm break-words">{item.name}</span>
                <span className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 whitespace-nowrap">
                  Từ PO: {item.po_code || item.po_id}
                </span>
              </div>
              <div className="text-xs whitespace-nowrap">
                Tồn khả dụng:{' '}
                <b className="text-emerald-700 tabular-nums">
                  {`${item.qty_on_hand.toLocaleString('vi-VN')} ${item.uom}`}
                </b>
              </div>
            </div>
          </div>

          {/* Target material selector (NO material code displayed!) */}
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-700">
              Chọn vật tư có sẵn đích <span className="text-rose-600">*</span>
            </label>
            <p className="mb-2 text-[11px] text-slate-500">
              Chỉ các vật tư đang hoạt động (Active). Chọn đúng 1 vật tư.
            </p>

            {/* Filter / Search input */}
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                value={materialSearch}
                onChange={(e) => setMaterialSearch(e.target.value)}
                placeholder="Tìm tên vật tư..."
                className="h-8 w-full rounded border border-slate-300 bg-white pl-8 pr-3 text-xs outline-none focus:border-slate-500"
              />
            </div>

            {/* Material select box */}
            <div className="max-h-44 overflow-y-auto rounded border border-slate-200 bg-white divide-y divide-slate-100">
              {loadingMaterials ? (
                <div className="flex items-center justify-center py-6 text-xs text-slate-400">
                  <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Đang tải danh sách vật tư...
                </div>
              ) : filteredMaterials.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">
                  Không tìm thấy vật tư hoạt động nào phù hợp
                </div>
              ) : (
                filteredMaterials.map((mat) => {
                  const isSelected = mat.id === selectedMaterialId
                  return (
                    <div
                      key={mat.id}
                      onClick={() => setSelectedMaterialId(mat.id)}
                      className={`flex cursor-pointer items-center justify-between p-2.5 text-xs transition-colors hover:bg-slate-50 ${
                        isSelected ? 'bg-blue-50/80 font-medium' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            isSelected
                              ? 'border-blue-600 bg-blue-600 text-white'
                              : 'border-slate-300 bg-white'
                          }`}
                        >
                          {isSelected ? <Check className="h-3 w-3 stroke-[3]" /> : null}
                        </div>
                        {/* Display ONLY material name and kind, NEVER material code */}
                        <span className="text-slate-900 break-words">{mat.name}</span>
                        <span className="rounded bg-slate-100 px-1 py-0.2 text-[10px] text-slate-600 whitespace-nowrap shrink-0">
                          {mat.kind === 'RAW' ? 'Nguyên liệu' : 'Phụ liệu'}
                        </span>
                      </div>

                      <div className="text-[11px] text-slate-500 whitespace-nowrap shrink-0 ml-2">
                        ĐVT: <b className="text-slate-700">{mat.uom_default || '—'}</b>
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            {selectedMaterial ? (
              <div className="mt-1.5 text-xs text-slate-600">
                Đã chọn: <b className="text-slate-900">{selectedMaterial.name}</b> (ĐVT:{' '}
                <b>{selectedMaterial.uom_default || '—'}</b>)
              </div>
            ) : null}
          </div>

          {/* Quantity to convert */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold text-slate-700">
                Số lượng chuyển ({item.uom}) <span className="text-rose-600">*</span>
              </label>
              <input
                type="number"
                min="0.0001"
                max={item.qty_on_hand}
                step="any"
                value={qtyFree || ''}
                onChange={(e) => setQtyFree(parseFloat(e.target.value) || 0)}
                onWheel={(e) => (e.target as HTMLElement).blur()}
                className="h-9 w-full rounded border border-slate-300 px-3 text-right text-xs font-semibold tabular-nums outline-none focus:border-slate-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="mt-0.5 block text-[11px] text-slate-400">
                Mặc định = tồn ({item.qty_on_hand.toLocaleString('vi-VN')} {item.uom})
              </span>
            </div>

            {/* Conversion Factor */}
            <div>
              {selectedMaterial ? (
                isSameUom ? (
                  <div className="flex h-full flex-col justify-center rounded border border-emerald-200 bg-emerald-50/60 p-2.5 text-xs text-emerald-800">
                    <span className="font-semibold">Cùng đơn vị tính ({item.uom})</span>
                    <span className="text-[11px] text-emerald-700">Tỉ lệ quy đổi 1 : 1 (không cần nhập hệ số)</span>
                  </div>
                ) : (
                  <div>
                    <label className="mb-1 block text-xs font-bold text-amber-900">
                      Hệ số quy đổi: 1 {item.uom} = ? {selectedMaterial.uom_default}{' '}
                      <span className="text-rose-600">*</span>
                    </label>
                    <input
                      type="number"
                      min="0.00000001"
                      step="any"
                      placeholder="Nhập hệ số quy đổi..."
                      value={factor}
                      onChange={(e) => setFactor(e.target.value)}
                      onWheel={(e) => (e.target as HTMLElement).blur()}
                      className="h-9 w-full rounded border border-amber-400 bg-amber-50/30 px-3 text-right text-xs font-bold tabular-nums outline-none focus:border-amber-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <span className="mt-0.5 block text-[11px] text-amber-800">
                      Khác ĐVT bắt buộc nhập hệ số quy đổi
                    </span>
                  </div>
                )
              ) : (
                <div className="flex h-full items-center text-xs text-slate-400 italic">
                  Vui lòng chọn vật tư đích ở trên
                </div>
              )}
            </div>
          </div>

          {/* Expected incoming result */}
          {selectedMaterial && expectedQtyBase != null ? (
            <div className="flex items-center justify-between rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <span className="text-slate-600">Số lượng vật tư sẽ nhập vào kho:</span>
              <span className="font-bold text-slate-950 tabular-nums">
                {expectedQtyBase.toLocaleString('vi-VN')} {selectedMaterial.uom_default}
              </span>
            </div>
          ) : null}

          {/* Reason input */}
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-700">
              Lý do chuyển sang vật tư <span className="text-rose-600">*</span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="VD: Chuẩn hóa vật tư sau khi kiểm định, Đưa vào định mức..."
              className="h-9 w-full rounded border border-slate-300 px-3 text-xs outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-[var(--color-border)] px-5 py-3">
          <Button variant="secondary" onClick={requestClose} disabled={submitting}>
            Hủy bỏ
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting || !selectedMaterialId || !reason.trim() || qtyFree <= 0}
            className="whitespace-nowrap"
          >
            {submitting ? (
              <>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Đang chuyển...
              </>
            ) : (
              'Xác nhận chuyển sang vật tư'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
